import cn from 'classnames'
import {AnimatePresence, motion} from 'framer-motion'
import {useEffect, useState} from 'react'
import {createPortal} from 'react-dom'

import {MKT_CONSENT} from 'src/common/constants'
import {BTN_PRIMARY, GAME_RULES_URL, GAME_TERMS_URL} from '../constants'
import {splitFullNameForHubspot} from '../hubspot'
import type {SubmitEmailData} from '../types'

export const JoinLeaderboardModal = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  serverError,
}: {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: SubmitEmailData) => Promise<void>
  isSubmitting?: boolean
  serverError?: string | null
}) => {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [company, setCompany] = useState('')
  const [marketingOptIn, setMarketingOptIn] = useState(false)
  const [error, setError] = useState('')
  const [fieldErrors, setFieldErrors] = useState<{name?: boolean; email?: boolean; company?: boolean}>({})

  // Clear errors when modal closes
  useEffect(() => {
    if (!isOpen) {
      setError('')
      setFieldErrors({})
    }
  }, [isOpen])

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow
      document.body.style.overflow = 'hidden'

      return () => {
        document.body.style.overflow = prev
      }
    }

    return undefined
  }, [isOpen])

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isOpen, onClose])

  const handleSubmit = async () => {
    const errors: {name?: boolean; email?: boolean; company?: boolean} = {}
    const messages: string[] = []

    if (!name.trim() || !splitFullNameForHubspot(name)) {
      errors.name = true
      messages.push('Please enter your first and last name.')
    }

    if (!email.trim()) {
      errors.email = true
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      errors.email = true
      messages.push('Please enter a valid email address.')
    }

    if (!company.trim()) {
      errors.company = true
    }

    setFieldErrors(errors)

    if (Object.keys(errors).length > 0) {
      setError(messages.length > 0 ? messages.join(' ') : 'Please fill in all required fields.')
      return
    }

    setError('')

    try {
      await onSubmit({name: name.trim(), email: email.trim(), company: company.trim(), marketingOptIn})
    } catch {
      // The parent owns server error state so the submitted values remain editable.
    }
  }

  const container = typeof document === 'undefined' ? null : document.getElementById('dialog-container')

  if (!container) {
    return null
  }

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-moss-black/[70%] backdrop-blur-[10px]"
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          exit={{opacity: 0}}
          transition={{duration: 0.25, ease: 'easeOut'}}
          onClick={onClose}
        >
          <motion.div
            className="relative mx-4 w-full max-w-[440px] rounded-2xl border border-grey-900 bg-[#171717] p-6 shadow-2xl"
            initial={{opacity: 0, y: 24, scale: 0.97}}
            animate={{opacity: 1, y: 0, scale: 1}}
            exit={{opacity: 0, y: 12, scale: 0.97}}
            transition={{type: 'spring', damping: 28, stiffness: 300, mass: 0.8}}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute right-4 top-4 flex size-8 items-center justify-center rounded-full text-grey-300 transition-colors hover:bg-grey-900 hover:text-grey-100"
              aria-label="Close"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>

            {/* Heading */}
            <h2 className="mb-2 text-20 font-medium text-grey-110">Join the competition</h2>
            <p className="mb-6 text-13 leading-5 text-grey-300">
              Enter your details to get on the leaderboard. This month&apos;s top scorer wins a{' '}
              <span className="font-semibold text-grey-200">&pound;150 Treatwell voucher</span>. Once you&apos;re in,
              share the game to play for <span className="font-semibold text-grey-200">Apple AirPods</span> instead.
            </p>

            {/* Form */}
            <div className="flex flex-col gap-3">
              <FloatingField
                label="Your name (required)"
                type="text"
                value={name}
                hasError={!!fieldErrors.name}
                onChange={(v) => {
                  setName(v)
                  setFieldErrors((prev) => ({...prev, name: false}))
                }}
              />
              <FloatingField
                label="Work email (required)"
                type="email"
                value={email}
                hasError={!!fieldErrors.email}
                onChange={(v) => {
                  setEmail(v)
                  setFieldErrors((prev) => ({...prev, email: false}))
                }}
              />
              <FloatingField
                label="Company name (required)"
                type="text"
                value={company}
                hasError={!!fieldErrors.company}
                onChange={(v) => {
                  setCompany(v)
                  setFieldErrors((prev) => ({...prev, company: false}))
                }}
              />
              <label className="flex cursor-pointer items-start gap-2.5 py-1">
                <input
                  type="checkbox"
                  checked={marketingOptIn}
                  onChange={(e) => setMarketingOptIn(e.target.checked)}
                  className="mt-0.5 size-4 shrink-0 cursor-pointer appearance-none rounded border border-grey-900 bg-[#0f0f0f] checked:border-green-neon checked:bg-green-neon"
                  style={{
                    backgroundImage: marketingOptIn
                      ? "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4 8L7 11L12 5' stroke='%23000' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")"
                      : 'none',
                    backgroundSize: '12px',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }}
                />
                <span className="text-12 leading-4 text-grey-450">
                  I&apos;d like to receive updates about Moss products and services. This is optional and won&apos;t
                  affect your participation. See our{' '}
                  <a
                    href={MKT_CONSENT.en.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-grey-450 underline transition-colors hover:text-grey-300"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms &amp; Conditions (Marketing)
                  </a>
                  .
                </span>
              </label>
              <p className={cn('text-12 text-ui-red-450', error || serverError ? 'visible' : 'invisible')}>
                {error || serverError || 'placeholder'}
              </p>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={cn(BTN_PRIMARY, 'w-full disabled:opacity-50')}
              >
                {isSubmitting ? 'Joining…' : 'Join leaderboard'}
              </button>
              <p className="text-center text-12 leading-4 text-grey-450">
                We won't show any of your personal data on the leaderboard.
              </p>
              <div className="mt-1 text-center text-12 text-grey-450">
                <a
                  href={GAME_TERMS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline transition-colors hover:text-grey-300"
                >
                  Game T&amp;C
                </a>
                {' | '}
                <a
                  href={GAME_RULES_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline transition-colors hover:text-grey-300"
                >
                  Game Rules
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    container,
  )
}

const FloatingField = ({
  label,
  type,
  value,
  hasError,
  onChange,
}: {
  label: string
  type: string
  value: string
  hasError?: boolean
  onChange: (v: string) => void
}) => {
  const [focused, setFocused] = useState(false)
  const active = focused || value.length > 0

  return (
    <div className="relative">
      <label
        className={cn(
          'pointer-events-none absolute left-3 transition-all duration-200',
          active ? 'top-1 text-10' : 'top-1/2 -translate-y-1/2 text-14',
          hasError ? 'text-ui-red-450' : 'text-grey-450',
        )}
      >
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className={cn(
          'h-12 w-full rounded-lg border bg-[#0f0f0f] px-3 text-14 text-grey-110 outline-none transition-colors',
          active ? 'pt-4' : '',
          hasError && 'border-ui-red-450',
          !hasError && focused && 'border-moss-white',
          !hasError && !focused && 'border-grey-900',
        )}
      />
    </div>
  )
}
